import { DataView, Loader, Pagination, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import ResultsFilter from '../../../../components/ResultsFilter/ResultsFilter';
import { useCustomNavigate } from '../../../../hooks/common';
import { UseFetchSearchQuery } from '../../../../hooks/searches/useFetchSearch';
import { IssuerSearchResultItemDto } from '../../../../services/dtos/issuer/issuerSearchResult.dto';
import { getIssuerRoute } from '../../../../utils/navigationUtils';
import { SearchResultCategory } from '../../SearchResults.constants';
import SearchResultTableHeader from '../SearchResultTableHeader/SearchResultTableHeader';
import { SEARCH_RESULT_TABLES_PAGINATION } from '../searchResultTables.logic';
import { viewConfig } from './CompaniesTable.logic';
import styles from './CompaniesTable.module.scss';

const cx = classNames.bind(styles);

interface CompaniesTableProps {
  query: UseFetchSearchQuery;
  activeCategory: SearchResultCategory;
  tableCategory: SearchResultCategory;
  onCategorySelect?: () => void;
  showItemsCount: number;
  onShowItemsCountChange: (count: number) => void;
  page: number;
  setPage: (value: number) => void;
}

const ROW_HEIGHT = 41;
const PAGINATION_HEIGHT = 45;

const CompaniesTable = ({
  query,
  showItemsCount,
  onShowItemsCountChange,
  activeCategory,
  tableCategory,
  onCategorySelect,
  page,
  setPage,
}: CompaniesTableProps): React.ReactElement | null => {
  const { navigateOnMouseUp } = useCustomNavigate();
  const { data, isLoading, isError, refetch, totalResults, totalPages } = query;
  const noResults = !isLoading && !data?.length && !isError;
  const isTableCategoryTabActive = activeCategory === tableCategory;
  const shouldDisplayTable = isTableCategoryTabActive || activeCategory === SearchResultCategory.All;
  const loaderHeight = isTableCategoryTabActive ? '60vh' : `${showItemsCount * ROW_HEIGHT + PAGINATION_HEIGHT}px`;

  const onRowClick = (company, event) => {
    navigateOnMouseUp(event, { route: getIssuerRoute(company.tagId) });
  };

  const renderPaginationFooter = () => {
    if (isLoading || !page || !totalPages || totalPages <= 1) {
      return null;
    }

    return (
      <div className={cx('footer')}>
        <Pagination activePage={page} onPageChange={setPage} totalPages={totalPages} />
      </div>
    );
  };

  const renderResultsFilter = () => (
    <ResultsFilter
      selectedValue={showItemsCount}
      values={
        !isTableCategoryTabActive
          ? SEARCH_RESULT_TABLES_PAGINATION.options
          : [...SEARCH_RESULT_TABLES_PAGINATION.options, ...SEARCH_RESULT_TABLES_PAGINATION.extendedOptions]
      }
      onResultsCountChange={onShowItemsCountChange}
      optionTestIdPrefix="companies-count-option"
      onShowAllSelected={!isTableCategoryTabActive ? onCategorySelect : undefined}
    />
  );

  const renderLoader = () => (
    <div style={{ height: loaderHeight }}>
      <Loader position="center" />
    </div>
  );

  const renderTable = () => (
    <DataView
      data={data as IssuerSearchResultItemDto[]}
      config={viewConfig(onRowClick, cx('cell'))}
      getKey={(company: IssuerSearchResultItemDto) => company.tagId}
    />
  );

  if (!shouldDisplayTable) {
    return null;
  }

  if (noResults) {
    return null;
  }

  return (
    <Widget
      headerTitle={<SearchResultTableHeader title="Companies" totalResults={totalResults} />}
      firstLine={renderResultsFilter()}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      footer={renderPaginationFooter()}
      showFooterDivider
      dataTestId="companies-table"
      fixedHeight
    >
      {isLoading ? renderLoader() : renderTable()}
    </Widget>
  );
};

export default CompaniesTable;
